import axios, { CancelTokenSource } from 'axios';

import { APIError, UnsupportedStructureError } from '~services/Errors';

import { GetLeadsResponseDecoder, LeadBulkActionOptions, LeadsResponse } from './domain';

export const getLeadsByNextUrl = async (
  nextUrl: string,
  cancelToken?: CancelTokenSource,
): Promise<LeadsResponse | undefined> => {
  const path = nextUrl;
  let resp;

  try {
    resp = await axios.request({
      url: path,
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      cancelToken: cancelToken ? cancelToken.token : undefined,
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return undefined;
    }

    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, (e.response!.data as { error: string }).error || e.message);
    }

    throw new APIError(-1, e as string);
  }

  const decoded = GetLeadsResponseDecoder.run(resp.data);

  if (decoded.ok === false) {
    const err = new UnsupportedStructureError(decoded.error.message);

    console.error(decoded.error);
    console.error(err);
    throw err;
  }

  return decoded.result;
};

export const getLeads = async (
  campaignId: number,
  listId: number,
  search: string | undefined,
  leadStatus: string[] | undefined,
  cancelToken?: CancelTokenSource,
): Promise<LeadsResponse | undefined> => {
  const path = `/api/campaign/${campaignId}/list/${listId}/lead`;
  const params = {
    search: search ? search.toLowerCase() : undefined,
    lead_status: leadStatus || undefined,
  };
  let resp;

  try {
    resp = await axios.request({
      url: path,
      method: 'GET',
      params: params,
      paramsSerializer: {
        indexes: null,
      },
      headers: {
        Accept: 'application/json',
      },
      cancelToken: cancelToken ? cancelToken.token : undefined,
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return undefined;
    }

    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, (e.response!.data as { error: string }).error || e.message);
    }

    throw new APIError(-1, e as string);
  }

  const decoded = GetLeadsResponseDecoder.run(resp.data);

  if (decoded.ok === false) {
    const err = new UnsupportedStructureError(decoded.error.message);

    console.error(decoded.error);
    console.error(err);
    throw err;
  }

  return decoded.result;
};

export const removeLead = async (campaignId: number, listId: number, leadId: number) => {
  const path = `/api/campaign/${campaignId}/list/${listId}/lead/${leadId}`;

  try {
    await axios.request({
      method: 'DELETE',
      url: path,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, e.message);
    }

    throw new APIError(-1, e as string);
  }
};

export const bulkRemoveLeads = async (campaignId: number, listId: number, data: LeadBulkActionOptions) => {
  const path = `/api/campaign/${campaignId}/list/${listId}/bulk-lead-remove`;
  const body = {
    select_all: data.selectAll,
    lead_ids: data.leadIds,
    excluded_lead_ids: data.excludedLeadIds,
    search: data.search || undefined,
    lead_status: data.leadStatus,
  };
  try {
    await axios.request({
      method: 'DELETE',
      url: path,
      data: body,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, e.message);
    }

    throw new APIError(-1, e as string);
  }
};
